import { computed, watchEffect } from 'vue';
import { useOrganization } from './useOrganization';
import Cookies from 'js-cookie';
import { STORAGE_KEYS } from '@/constants/StorageKeys';

export interface Branding {
    logoUrl: string;
    faviconUrl: string;
    primaryColor: string;
}

const defaultBranding: Branding = {
    logoUrl: '/logo.png',
    faviconUrl: '/favicon.ico',
    primaryColor: '#ef4444',
};

// Helper to convert hex to RGB object
function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}

// Helper to mix color with white (tint) or black (shade)
// weight: 0 to 1. positive for white mixing (tint), negative for black mixing (shade)
function mix(color: { r: number; g: number; b: number }, weight: number) {
    const target = weight > 0 ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };
    const w = Math.abs(weight);

    return {
        r: Math.round(color.r * (1 - w) + target.r * w),
        g: Math.round(color.g * (1 - w) + target.g * w),
        b: Math.round(color.b * (1 - w) + target.b * w),
    };
}

export function useBranding() {
    const { organization, getById, chooseOrganization } = useOrganization();

    const branding = computed<Branding>(() => {
        if (!organization) {
            return defaultBranding;
        }

        return {
            logoUrl: organization.logo || defaultBranding.logoUrl,
            faviconUrl: organization.favicon || defaultBranding.faviconUrl,
            primaryColor: organization.color || defaultBranding.primaryColor,
        };
    });

    const updateFavicon = (url: string) => {
        // Remove existing icon links to prevent conflicts
        const links = document.querySelectorAll("link[rel*='icon']");
        links.forEach((link) => link.parentNode?.removeChild(link));

        // Create new link
        const link = document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        // Add timestamp to force varying cache if it's an update
        link.href = `${url}?v=${new Date().getTime()}`;
        document.head.appendChild(link);
    };

    const updateThemeColors = (colorHex: string) => {
        const rgb = hexToRgb(colorHex);
        if (!rgb) return;

        const root = document.documentElement;

        const shades = {
            50: mix(rgb, 0.95),
            100: mix(rgb, 0.9),
            200: mix(rgb, 0.75),
            300: mix(rgb, 0.5),
            400: mix(rgb, 0.25),
            500: rgb, // Base
            600: mix(rgb, -0.1),
            700: mix(rgb, -0.3),
            800: mix(rgb, -0.5),
            900: mix(rgb, -0.7),
            950: mix(rgb, -0.85),
        };

        Object.entries(shades).forEach(([shade, color]) => {
            root.style.setProperty(`--color-red-${shade}`, `${color.r} ${color.g} ${color.b}`);
        });
    };

    const init = () => {
        const token = Cookies.get(STORAGE_KEYS.TOKEN);

        // Fetch organization data on init ONLY if we have a token
        if (token && organization?.id) {
            getById(organization.id)
                .then((org) => {
                    if (org) {
                        chooseOrganization(org);
                    }
                })
                .catch(console.error);
        }

        watchEffect(() => {
            if (branding.value) {
                updateFavicon(branding.value.faviconUrl);
                updateThemeColors(branding.value.primaryColor);
            }
        });
    };

    return {
        branding,
        init,
    };
}
